import { useCallback, useMemo, useState } from "react";
import type { RowBase } from "../data-table.js";
import type { ColumnDef, DataTableFilters, FilterData } from "../data-table.types.js";
import { matchesFilter } from "../utils/data-table-utils.js";

export const useDataTableFiltering = <TRow extends RowBase>(
  data: TRow[],
  columns: ColumnDef<TRow>[]
) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<DataTableFilters<TRow>>({});

  const toggleFilter = useCallback((columnKey: keyof TRow, value: string | number) => {
    setFilters((prev) => {
      const currentFilter = prev[columnKey];
      const currentValues = currentFilter?.values || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      return {
        ...prev,
        [columnKey]: newValues.length > 0 ? { values: newValues } : undefined,
      };
    });
  }, []);

  const setColumnFilter = useCallback(
    (columnKey: keyof TRow, values: (string | number)[], nestedKey?: string) => {
      setFilters((prev) => ({
        ...prev,
        [columnKey]: values.length > 0 ? { values, nestedKey } : undefined,
      }));
    },
    []
  );

  const searchRows = useCallback(
    (row: TRow) => {
      if (!searchTerm) return true;

      const searchableColumns = columns.filter((col) => col.searchable);
      return searchableColumns.some((col) => {
        const value = col.searchValue ? col.searchValue(row) : row[col.key];
        return String(value).toLowerCase().includes(searchTerm.toLowerCase());
      });
    },
    [searchTerm, columns]
  );

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (!searchRows(row)) return false;

      for (const [columnKey, filterData] of Object.entries(filters)) {
        if (!filterData || filterData.values.length === 0) continue;

        if (!matchesFilter(row, columnKey as keyof TRow, filterData as FilterData)) {
          return false;
        }
      }
      return true;
    });
  }, [data, searchRows, filters]);

  return {
    searchTerm,
    setSearchTerm,
    filters,
    toggleFilter,
    setColumnFilter,
    filteredData,
  };
};
